import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Popover,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { CalendarToday, Security } from '@mui/icons-material';
import { DateRangePicker } from 'react-date-range';
import { addDays, format, isSameDay } from 'date-fns';
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar
} from 'recharts';
import { authenticatedFetch } from '../utils/api';

// Custom Tooltip Component
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload;
    return (
      <Box
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          padding: '12px 16px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          minWidth: '180px'
        }}
      >
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 600, 
            color: '#333',
            mb: 1,
            borderBottom: '1px solid #eee',
            pb: 1
          }}
        >
          {data?.date}
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#40518A', 
            fontWeight: 500
          }}
        >
          Intrusions: {data?.intrusions?.toLocaleString() || 0}
        </Typography>
      </Box>
    );
  }
  return null;
};

const IntrusionDailyStatsCard = () => {
  const [dateRange, setDateRange] = useState([
    {
      startDate: addDays(new Date(), -6),
      endDate: new Date(),
      key: 'selection'
    }
  ]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(7);

  const buildParams = () => {
    const params = new URLSearchParams();
    params.append('start_date', format(dateRange[0].startDate, 'yyyy-MM-dd'));
    params.append('end_date', format(dateRange[0].endDate, 'yyyy-MM-dd'));
    return params.toString();
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authenticatedFetch(`/dashboard/intrusion-daily-stats?${buildParams()}`);
      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch intrusion daily stats:', err);
      setError(err.message || 'Unable to load intrusion daily stats');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCalendar = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleCloseCalendar = () => {
    setOpen(false);
  };

  const handleDateRangeChange = (ranges) => {
    setDateRange([ranges.selection]);
  };

  const handleApplyDateRange = () => {
    setSelectedPreset(null);
    fetchStats();
    handleCloseCalendar();
  };

  const handlePresetChange = (event, newPreset) => {
    if (newPreset !== null) {
      setSelectedPreset(newPreset);
      const newDateRange = [{
        startDate: addDays(new Date(), -newPreset),
        endDate: new Date(),
        key: 'selection'
      }];
      setDateRange(newDateRange);
    }
  };

  // Fetch when preset changes
  useEffect(() => {
    if (selectedPreset !== null) {
      fetchStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPreset]);

  const filledData = useMemo(() => {
    if (!stats?.daily_counts || !stats.date_range) {
      return [];
    }
    
    const startDate = new Date(stats.date_range.start_date);
    const endDate = new Date(stats.date_range.end_date);
    const allDates = [];
    const currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      const existingData = stats.daily_counts.find(d => d.date === dateStr);
      allDates.push({
        date: dateStr,
        displayDate: format(currentDate, 'MMM dd'),
        intrusions: existingData ? existingData.intrusions : 0
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return allDates;
  }, [stats]);

  const daysDiff = useMemo(() => {
    if (!dateRange[0]?.startDate || !dateRange[0]?.endDate) return 7;
    return Math.ceil((dateRange[0].endDate - dateRange[0].startDate) / (1000 * 60 * 60 * 24));
  }, [dateRange]);

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ pb: 0, '&:last-child': { pb: 0 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Security color="primary" />
            <Typography variant="h6">Daily Stats</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                px: 2,
                py: 1
              }}
            >
              <ToggleButtonGroup
                value={selectedPreset}
                exclusive
                onChange={handlePresetChange}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    px: { xs: 1, sm: 1.5 },
                    py: 0.5,
                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                    textTransform: 'none',
                    '&.Mui-selected': {
                      backgroundColor: '#40518A',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#364775',
                      },
                    },
                  },
                }}
              >
                <ToggleButton value={7}>7 Days</ToggleButton>
                <ToggleButton value={15}>15 Days</ToggleButton>
                <ToggleButton value={30}>30 Days</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                px: 1.2,
                py: 0.85,
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main',
                },
              }}
              onClick={handleOpenCalendar}
            >
              <CalendarToday sx={{ color: '#666', fontSize: 18 }} />
              <Typography variant="body2" sx={{ color: '#333', fontSize: '0.875rem' }}>
                {isSameDay(dateRange[0].startDate, dateRange[0].endDate)
                  ? format(dateRange[0].startDate, 'MMM dd, yyyy')
                  : `${format(dateRange[0].startDate, 'MMM dd')} - ${format(dateRange[0].endDate, 'MMM dd, yyyy')}`}
              </Typography>
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenCalendar(e);
                }} 
                sx={{ ml: 0.5 }}
              >
                <CalendarToday fontSize="small" />
              </IconButton>
            </Box>
            <Button
              variant="contained"
              onClick={fetchStats}
              disabled={loading}
              sx={{ bgcolor: '#40518A', '&:hover': { bgcolor: '#364775' } }}
            >
              Apply
            </Button>
          </Box>
        </Box>

        {/* Date Range Picker Popover */}
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleCloseCalendar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ p: 0, width: 'fit-content' }}>
            <DateRangePicker
              ranges={dateRange}
              onChange={handleDateRangeChange}
              moveRangeOnFirstSelection={false}
              months={1}
              direction="horizontal"
              rangeColors={['#40518A']}
              staticRanges={[]}
              inputRanges={[]}
              maxDate={new Date()}
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', p: 2, pt: 0 }}>
              <Button onClick={handleCloseCalendar} variant="outlined" size="small">
                Cancel
              </Button>
              <Button
                onClick={handleApplyDateRange}
                variant="contained"
                size="small"
                sx={{ bgcolor: '#40518A', '&:hover': { bgcolor: '#364775' } }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Popover>

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && !loading && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Chart */}
        {!loading && !error && (
          <>
            {filledData.length > 0 ? (
              <Box
                sx={{
                  '& .recharts-wrapper:focus, & .recharts-surface:focus, & .recharts-rectangle:focus': {
                    outline: 'none',
                  },
                }}
              >
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart 
                    data={filledData}
                    margin={{ 
                      top: 20, 
                      right: 10, 
                      left: 0, 
                      bottom: 0
                    }}
                    barGap={0}
                    barCategoryGap="20%"
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="displayDate" 
                      angle={daysDiff > 15 ? -45 : -35} 
                      textAnchor="end" 
                      height={daysDiff > 15 ? 80 : 60}
                      tick={{ fontSize: daysDiff > 15 ? 10 : 12 }}
                      interval={daysDiff > 15 ? 1 : 0}
                    />
                    <YAxis allowDecimals={false} />
                    <Tooltip 
                      cursor={{ stroke: '#000', fill: 'none', strokeWidth: 0 }}
                      content={<CustomTooltip />}
                    />
                    <Legend 
                      wrapperStyle={{ 
                        paddingTop: '10px',
                        fontSize: '14px'
                      }}
                    />
                    <Bar 
                      dataKey="intrusions" 
                      name="Intrusions" 
                      fill="#f59e0b" 
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 400,
                  border: '1px dashed #ccc',
                  borderRadius: 1,
                  bgcolor: '#f5f5f5'
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  No intrusion data available for the selected date range
                </Typography>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default IntrusionDailyStatsCard;
